import React, { useEffect, useRef, useState } from 'react'
import ProductList from '@components/ProductExplorer/ProductList'
import ProductListSkeleton from '@components/ProductExplorer/ProductListSkeleton'

import PageCounter from '../PageCounter'
import ProductExplorerFilterList from '../ProductExplorerFilterList'
import { FilterConfig, ProductExplorerTexts, ProductSnapshot } from '../ProductExplorerTypes'

interface ProductExplorerLayoutProperties {
  totalProductItems?: number
  pageSize: number
  currentPage: number
  productList: ProductSnapshot[]
  maxDisplayedElementCount: number
  onPageChange: (nextPage: number) => void
  filtersConfig?: FilterConfig[]
  activeFilters: Record<string, Set<string>>
  productBaseUrl: string
  texts: ProductExplorerTexts
  isPending: boolean
  onOptionsChange: (filterId: string, newValue: Set<string>) => void
  onResetAllFilters: () => void
  onTextSearchChange: React.Dispatch<React.SetStateAction<string>>
  textSearch: string
  textSearchLabel?: string
}

// Mirrors ProductExplorerPreRenderedHtmlService.getFormattedProductsFoundLabel so the
// server-rendered markup, the visible label below, and the screen reader announcement all
// produce the same text. `countString` lets the caller pass either the plain number (for the
// announcement) or markup wrapping it (for the visible label).
function formatProductsFound(count: number, texts: ProductExplorerTexts, countString: string) {
  return count > 1
    ? (texts.pluralProductsFound || '').replace('{number}', countString)
    : `${countString} ${texts.singularProductFound || ''}`.trim()
}

function productsResultText(count: number, texts: ProductExplorerTexts) {
  const countMarkup = `<span class="cmp-product-explorer-layout__product-count">${count}</span>`
  const totalProductsFoundText = formatProductsFound(count, texts, countMarkup)

  return (
    <p
      className="cmp-product-explorer-layout__total-count"
      dangerouslySetInnerHTML={{ __html: totalProductsFoundText }}
    ></p>
  )
}

// Plain text equivalent of what ProductsResultSection renders, for the live region.
function productsFoundAnnouncement(totalProductItems: number | undefined, texts: ProductExplorerTexts) {
  const count = totalProductItems || 0
  if (count === 0) {
    return texts.noProductsFoundTitle
  }

  return formatProductsFound(count, texts, count.toString())
}

function ProductsResultSection({
  totalProductItems,
  texts,
}: {
  totalProductItems?: number
  texts: ProductExplorerTexts
}) {
  // An unknown count is not the same as zero: while the count is still loading, reserve a single
  // line so this row keeps its height, instead of showing "no products found" and then swapping.
  if (totalProductItems === undefined) {
    return <p className="cmp-product-explorer-layout__total-count">&nbsp;</p>
  }

  if (totalProductItems === 0) {
    return (
      <span className="cmp-product-explorer-layout__total-count-label">
        <div>{texts.noProductsFoundTitle}</div>
        <div>{texts.noProductsFoundDescription}</div>
      </span>
    )
  }

  return productsResultText(totalProductItems, texts)
}

const ProductExplorerLayout: React.FC<ProductExplorerLayoutProperties> = ({
  productList,
  currentPage,
  pageSize,
  totalProductItems,
  maxDisplayedElementCount,
  activeFilters,
  filtersConfig,
  productBaseUrl,
  texts,
  isPending,
  onPageChange,
  onOptionsChange,
  onResetAllFilters,
  onTextSearchChange,
  textSearch,
  textSearchLabel,
}: ProductExplorerLayoutProperties) => {
  const explorerReference = useRef(null)
  // `id` is bumped on every settled result so that an unchanged message (e.g. a different filter
  // that still yields 51 products) still replaces the text node and gets re-announced.
  const [announcement, setAnnouncement] = useState({ id: 0, message: '' })

  useEffect(() => {
    if (isPending) {
      return
    }
    setAnnouncement((old) => ({ id: old.id + 1, message: productsFoundAnnouncement(totalProductItems, texts) }))
  }, [isPending, totalProductItems, texts])

  // Reserve exactly as many placeholder cards as this page will show, so the last page (which may
  // be partially filled) is not over-reserved. Falls back to a full page while the count is unknown.
  const remainingOnThisPage =
    totalProductItems === undefined ? pageSize : totalProductItems - (currentPage - 1) * pageSize
  const skeletonCardCount = Math.max(1, Math.min(pageSize, remainingOnThisPage))

  const pageCounterAriaLabel = {
    paginationAriaLabel: texts.paginationAriaLabel || 'Pagination, Page {0} of {1}',
    paginationScreenReaderTextGoToPage: texts.paginationScreenReaderTextGoToPage || 'Go to page ',
    paginationScreenReaderTextNextPage: texts.paginationScreenReaderTextNextPage || 'Next Page',
    paginationScreenReaderTextPreviousPage: texts.paginationScreenReaderTextPreviousPage || 'Previous Page',
  }

  return (
    <div className="cmp-product-explorer-layout" ref={explorerReference} aria-busy={isPending}>
      {/*
        Must stay mounted for the lifetime of the component: a live region only announces changes
        that happen while it is already in the DOM, and the results below are unmounted on every
        fetch. Only the keyed child is swapped.
      */}
      <div className="sr-only" role="alert" aria-live="assertive" aria-atomic="true">
        <span key={announcement.id}>{announcement.message}</span>
      </div>
      <h1 className="cmp-product-explorer-layout__title">{texts.sectionTitle}</h1>
      {texts?.sectionLeadText && <h2 className="cmp-product-explorer-layout__lead-text">{texts?.sectionLeadText}</h2>}
      {isPending && !filtersConfig ? (
        // No seed and no filters yet: still reserve the grid's height rather than collapsing to a
        // spinner. The filter bar itself can't be reserved until we know how many filters there are.
        <ProductListSkeleton count={skeletonCardCount} />
      ) : (
        <>
          <div className="cmp-product-explorer-layout__header">
            <ProductExplorerFilterList
              activeFilters={activeFilters}
              filtersConfig={filtersConfig || []}
              applyLabel={texts.applyFilter}
              resetLabel={texts.resetFilter}
              onOptionsChange={onOptionsChange}
              onTextSearchChange={onTextSearchChange}
              textSearch={textSearch}
              textSearchLabel={textSearchLabel}
            />
            <button
              className="cmp-product-explorer-layout__reset-all"
              aria-label={texts.resetFiltersAria}
              onClick={onResetAllFilters}
            >
              <i className="ri-arrow-go-back-line" />
              {texts.resetFilters}
            </button>
          </div>
          <ProductsResultSection totalProductItems={totalProductItems} texts={texts} />
          {isPending && productList.length === 0 ? (
            <ProductListSkeleton count={skeletonCardCount} />
          ) : (
            <ProductList products={productList} productBaseUrl={productBaseUrl} texts={texts} />
          )}
          <PageCounter
            currentPage={currentPage}
            maxDisplayedElementCount={maxDisplayedElementCount}
            pageSize={pageSize}
            totalElements={totalProductItems || 0}
            onPageChange={onPageChange}
            reference={explorerReference}
            areaLabels={pageCounterAriaLabel}
          />
        </>
      )}
    </div>
  )
}

export default ProductExplorerLayout
